/**
 *  @class
 *  @function SlideShow
 */
if (!customElements.get('slide-show')) {
  class SlideShow extends HTMLElement {
    constructor() {
      super();
      this._boundPrev = () => this.flkty && this.flkty.previous();
      this._boundNext = () => this.flkty && this.flkty.next();
    }

    connectedCallback() {
      this.init();

      if (Shopify.designMode) {
        // Editor-only: Shopify patches this section's markup while you
        // change settings (e.g. switching height mode) without a full
        // page reload. Flickity options like adaptiveHeight are only
        // read once when the instance is created, so simply resizing a
        // stale instance isn't enough — rebuild it from scratch so the
        // new settings actually take effect immediately.
        this._onSectionLoad = (event) => {
          if (event.target.contains(this)) {
            this.init();
          }
        };
        document.addEventListener('shopify:section:load', this._onSectionLoad);

        this._onBlockSelect = (event) => {
          const slides = Array.from(this.querySelectorAll('.carousel__slide'));
          const index = slides.indexOf(event.target);
          if (this.flkty) this.flkty.select(index);
        };
        this.addEventListener('shopify:block:select', this._onBlockSelect);
      }
    }

    disconnectedCallback() {
      if (this._onSectionLoad) {
        document.removeEventListener('shopify:section:load', this._onSectionLoad);
      }
      if (this.flkty) {
        this.flkty.destroy();
      }
    }

    init() {
      const slideshow = this;

      if (this.flkty) {
        this.flkty.destroy();
        this.flkty = null;
      }

      const slides = Array.from(slideshow.querySelectorAll('.carousel__slide'));
      const dots = slideshow.dataset.dots === 'true';
      const autoplay = slideshow.dataset.autoplay === 'false' ? false : parseInt(slideshow.dataset.autoplay, 10);
      const align = slideshow.dataset.align === 'center' ? 'center' : 'left';
      const fade = slideshow.dataset.fade === 'true';
      const prevButton = slideshow.querySelector('.flickity-prev');
      const nextButton = slideshow.querySelector('.flickity-next');
      const customDots = slideshow.querySelector('.flickity-page-dots');
      const rightToLeft = document.dir === 'rtl';
      const args = {
        wrapAround: true,
        cellAlign: align,
        pageDots: dots,
        contain: true,
        fade: fade,
        autoPlay: autoplay,
        rightToLeft: rightToLeft,
        prevNextButtons: false,
        cellSelector: '.carousel__slide',
        on: {}
      };

      if (slides.length < 1) return;

      if (slideshow.classList.contains('main-slideshow')) {
        if (
          slideshow.classList.contains('desktop-height-image') ||
          slideshow.classList.contains('mobile-height-image') ||
          slideshow.classList.contains('desktop-height-auto') ||
          slideshow.classList.contains('mobile-height-auto')
        ) {
          args.adaptiveHeight = true;
        }

        args.on.ready = function () {
          const flkty = this;

          flkty.resize();

          // Custom Dots.
          if (dots && customDots) {
            const dotEls = customDots.querySelectorAll('li');
            dotEls.forEach((dot, i) => {
              dot.addEventListener('click', () => {
                flkty.select(i);
              });
            });
            dotEls[this.selectedIndex].classList.add('is-selected');
          }

          // Video Support.
          const videoContainer = flkty.cells[0].element.querySelector('.slideshow__slide-video-bg');
          if (videoContainer) {
            const iframe = videoContainer.querySelector('iframe');
            const video = videoContainer.querySelector('video');

            const loadVideo = () => {
              if (iframe && iframe.classList.contains('lazyload')) {
                iframe.addEventListener('lazybeforeunveil', () => {
                  slideshow.videoPlay(videoContainer);
                });
                lazySizes.loader.checkElems();
              } else if (video) {
                slideshow.videoPlay(videoContainer);
              }
            };

            if (iframe) {
              iframe.onload = loadVideo;
            } else if (video) {
              video.onloadstart = loadVideo;
            }
          }
        };

        args.on.change = function (index) {
          const flkty = this;

          // Color changes.
          const colorText = getComputedStyle(this.selectedElement).getPropertyValue('--color-body');
          const dotsWrapper = slideshow.querySelector('.flickity-custom-navigation-wrapper');
          if (dotsWrapper) {
            dotsWrapper.style.setProperty('--color-body', colorText);
          }

          // Custom Dots.
          if (dots && customDots) {
            const dotEls = customDots.querySelectorAll('li');
            dotEls.forEach((dot) => {
              dot.classList.remove('is-selected');
            });
            dotEls[this.selectedIndex].classList.add('is-selected');
          }

          // AutoPlay
          if (autoplay) {
            flkty.stopPlayer();
            flkty.playPlayer();
          }

          // Video Support.
          const previousIndex = fizzyUIUtils.modulo(this.selectedIndex - 1, this.slides.length);

          const videoContainerPrev = flkty.cells[previousIndex].element.querySelector('.slideshow__slide-video-bg');
          if (videoContainerPrev) {
            slideshow.videoPause(videoContainerPrev);
          }

          const videoContainer = flkty.cells[index].element.querySelector('.slideshow__slide-video-bg');
          if (videoContainer) {
            slideshow.videoPlay(videoContainer);
          }
        };
      }

      this.flkty = new Flickity(slideshow, args);

      if (prevButton) {
        prevButton.removeEventListener('click', this._boundPrev);
        prevButton.removeEventListener('keyup', this._boundPrev);
        nextButton.removeEventListener('click', this._boundNext);
        nextButton.removeEventListener('keyup', this._boundNext);

        prevButton.addEventListener('click', this._boundPrev);
        prevButton.addEventListener('keyup', this._boundPrev);
        nextButton.addEventListener('click', this._boundNext);
        nextButton.addEventListener('keyup', this._boundNext);
      }
    }

    videoPause(videoContainer) {
      setTimeout(() => {
        if (videoContainer.dataset.provider === 'hosted') {
          videoContainer.querySelector('video').pause();
        } else if (videoContainer.dataset.provider === 'youtube') {
          videoContainer.querySelector('iframe').contentWindow.postMessage(JSON.stringify({
            event: "command",
            func: "pauseVideo",
            args: ""
          }), "*");
        } else if (videoContainer.dataset.provider === 'vimeo') {
          videoContainer.querySelector('iframe').contentWindow.postMessage(JSON.stringify({
            method: "pause"
          }), "*");
        }
      }, 10);
    }

    videoPlay(videoContainer) {
      setTimeout(() => {
        if (videoContainer.dataset.provider === 'hosted') {
          videoContainer.querySelector('video').play();
        } else if (videoContainer.dataset.provider === 'youtube') {
          videoContainer.querySelector('iframe').contentWindow.postMessage(JSON.stringify({
            event: "command",
            func: "playVideo",
            args: ""
          }), "*");
        } else if (videoContainer.dataset.provider === 'vimeo') {
          videoContainer.querySelector('iframe').contentWindow.postMessage(JSON.stringify({
            method: "play"
          }), "*");
        }
      }, 10);
    }
  }

  customElements.define('slide-show', SlideShow);
}